import { BaseMessage, HumanMessage } from "../chat/types";
import { AnyMessage } from "../chat/types";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import { RootState } from "../store";
import { FileData, PingRequest } from "../types/api";
import { ConfigData, LocaleSettings } from "../types/core";
import { rateMessage } from "../actions/sharedActions";

export interface Conversation {
  conversation_id: string;
  created_at: string; // "2025-01-20T15:53:30.951643Z",
  messages: AnyMessage[];
  rating_score: number; // -1 to 5; -1 is unoffered; 0 is offered by not choosen; 1-5 is chosen rating
  rating_comment: string;
  rating_offered_at: string | null; // "2025-01-20T15:53:30.951643Z" or null if not offered
}

export interface ChatState {
  messages: AnyMessage[];
  conversationId: string | undefined;
  isLoading: boolean;
  apiError: string;
  overrides: any;
  enableRating: boolean;
  ratingPostponedUntil: number | null; // Unix timestamp in ms
}

const initialState: ChatState = {
  messages: [],
  conversationId: "",
  isLoading: false,
  apiError: "",
  overrides: null,
  enableRating: true,
  ratingPostponedUntil: null,
};

// Async thunk for sending messages
export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async ({
    value,
    conversationId,
    attachments,
    config,
    extraHeaders,
    pingRequestData,
    previousMessages,
    tags,
    overrides,
  }: {
    value: string;
    conversationId: string | undefined;
    attachments: FileData[];
    config: ConfigData;
    extraHeaders: [string, string][];
    pingRequestData: PingRequest;
    previousMessages: AnyMessage[];
    tags: any;
    overrides?: any;
  }) => {
    const url = `${config.apiBase}/v1/frames/${config.frameId}/conversations`;

    const newMessage: HumanMessage = {
      sender: "user",
      role: "user",
      content: value,
      // mock values:
      message_id: uuid(),
      attachments,
      metadata: {},
    };

    const payload = {
      ping: {
        user_data: pingRequestData.user_data,
        locales: pingRequestData.locales,
        locale: pingRequestData.locale,
        timezone: pingRequestData.timezone,
        timezone_offset: pingRequestData.timezone_offset,
        browser_url: pingRequestData.browser_url,
      },
      messages: [...previousMessages, newMessage],
      tags: tags,
      conversation_id: conversationId,
      overrides,
    };

    const headers = Array.from(
      new Headers({
        ...pingRequestData.extra_headers,
        ...Object.fromEntries(extraHeaders),
        "X-Device-Id": pingRequestData.device_id,
        ...Object.fromEntries(
          [
            ["X-User-Id", pingRequestData.user_id],
            ["X-Session-Id", pingRequestData.session_id],
          ].filter(([, v]) => !!v)
        ),
      }).entries()
    );

    return { newMessage, url, headers, payload };
  }
);

const SHOW_RATING_AFTER_LAST_MESSAGE_SECONDS = 120; // 2 minutes

export function initialMessagesToChatMessages(
  messages: BaseMessage[]
): AnyMessage[] {
  const transform = (m: BaseMessage) => {
    if (m.role === "assistant") {
      return {
        sender: m.sender,
        role: "assistant" as const,
        content: m.content,
        isGenerating: false,
        isInitial: m.sender !== "assistant",
        rating: null,
        message_id: uuid(),
        metadata: { handoff: null, rating: null },
      };
    } else if (m.role == "user") {
      return {
        sender: m.sender,
        role: "user" as const,
        content: m.content,
        isInitial: m.sender !== "user",
        message_id: uuid(),
        metadata: {},
      };
    } else {
      throw new Error("Invalid role");
    }
  };
  return messages.map(transform);
}
const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setInitialMessages: (
      state,
      action: PayloadAction<LocaleSettings["messages"]>
    ) => {
      state.messages = initialMessagesToChatMessages(action.payload);
    },
    prependInitialMessages: (
      state,
      action: PayloadAction<LocaleSettings["messages"]>
    ) => {
      state.messages = [
        ...initialMessagesToChatMessages(action.payload),
        ...state.messages,
      ];
    },
    appendInitialMessages: (
      state,
      action: PayloadAction<{
        messages: BaseMessage[];
        replaceInitialTail?: boolean;
      }>
    ) => {
      const { messages, replaceInitialTail = true } = action.payload;
      const newMessages = initialMessagesToChatMessages(messages);
      let keepUntil = state.messages.length;
      if (replaceInitialTail)
        keepUntil = state.messages.findLastIndex((msg) => !msg.isInitial) + 1;
      state.messages = [...state.messages.slice(0, keepUntil), ...newMessages];
    },
    updateStreamingResponse: (
      state,
      action: PayloadAction<{
        text: string;
        metadata: any;
        messageId: string;
      }>
    ) => {
      const { text, metadata, messageId } = action.payload;

      const existingMessageIndex = state.messages.findIndex(
        (m) => m.message_id === messageId
      );

      if (existingMessageIndex === -1) {
        state.messages.push({
          sender: "assistant",
          role: "assistant",
          content: text,
          message_id: messageId,
          isGenerating: true,
          isInitial: false,
          rating: null,
          metadata: metadata,
        });
      } else {
        state.messages[existingMessageIndex] = {
          ...state.messages[existingMessageIndex],
          content: text,
          metadata: {
            ...state.messages[existingMessageIndex].metadata,
            ...metadata,
          },
        };
      }
    },
    finishStreaming: (state, action: PayloadAction<{ messageId: string }>) => {
      const { messageId } = action.payload;
      const messageIndex = state.messages.findIndex(
        (m) => m.message_id === messageId
      );
      if (messageIndex === -1) {
        throw new Error(`Message ${messageId} not found ${state.messages}`);
      }
      if (state.messages[messageIndex].role === "user") {
        throw new Error(
          `Message ${messageId} is a user message - indicates logic error`
        );
      }
      state.messages[messageIndex] = {
        ...state.messages[messageIndex],
        isGenerating: false,
        isInitial: false,
      };
      state.isLoading = false;
    },
    setConversationId: (state, action: PayloadAction<string | undefined>) => {
      // NOTE(liamvdv): do not use this to change conversation, use setConversation instead.
      state.conversationId = action.payload;
    },
    setApiError: (state, action: PayloadAction<string>) => {
      state.apiError = action.payload;
      state.isLoading = false;
    },
    clearChat: (state) => {
      state.messages = [];
      state.conversationId = "";
      state.apiError = "";
      state.ratingPostponedUntil = null;
    },
    setConversation(state, action: PayloadAction<Conversation>) {
      // Clear messages if conversation ID changes
      state.messages = [];
      state.conversationId = action.payload.conversation_id;
      state.apiError = "";
      state.isLoading = false;
    },
    setMessages(state, action: PayloadAction<AnyMessage[]>) {
      state.messages = [...action.payload];
    },
    setOverrides: (state, action: PayloadAction<any>) => {
      state.overrides = action.payload;
    },
    postponeRating: (state, action: PayloadAction<number>) => {
      // Accepts ms duration (e.g. 300000 for 5 minutes)
      state.ratingPostponedUntil = Date.now() + action.payload;
    },
    resetRatingPostponement: (state) => {
      state.ratingPostponedUntil = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.apiError = "";
        state.enableRating = false;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload.newMessage);
        // Set 2-minute postponement after each user message
        state.ratingPostponedUntil =
          Date.now() + SHOW_RATING_AFTER_LAST_MESSAGE_SECONDS * 1000;
        state.enableRating = true;
      })
      .addCase(rateMessage.fulfilled, (state, action) => {
        const { messageId, rating } = action.payload;
        const messageIndex = state.messages.findIndex(
          (m) => m.message_id === messageId
        );
        if (messageIndex === -1)
          throw new Error(`Message ${messageId} not found`);
        if (state.messages[messageIndex].role === "user") {
          throw new Error(
            `Message ${messageId} is a user message - indicates logic error`
          );
        }
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          rating,
        };
      });
  },
});

export const {
  setInitialMessages,
  prependInitialMessages,
  updateStreamingResponse,
  finishStreaming,
  setConversationId,
  setApiError,
  clearChat,
  setConversation,
  setMessages,
  appendInitialMessages,
  setOverrides,
  postponeRating,
  resetRatingPostponement,
} = chatSlice.actions;

// Selectors
export const selectMessages = (state: RootState) => state.chat.messages;
export const selectConversationId = (state: RootState) =>
  state.chat.conversationId;
export const selectIsLoading = (state: RootState) => state.chat.isLoading;
export const selectApiError = (state: RootState) => state.chat.apiError;
export const selectOverrides = (state: RootState) => state.chat.overrides;

export const chatReducer = chatSlice.reducer;
